import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import { LocalizedString, Unit } from '@synergy/models';
import { PoolButton, PoolModal } from '@synergy/components';
import { makeStyles } from '@mui/styles';

import { ConfigurationDetailForm } from '../../components/configuration-detail-form';
import { UnitFormikValues } from '../models/unit-formik-values';
import { createUnit, patchUnit } from '../../../../common/actions/unit-actions';
import { unitsModalValidationSchema } from './units-detail-modal-validation-schema';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const useStyles = makeStyles(() => ({
    actionChildren: {
        minWidth: 200,
    },
}));

interface UnitsDetailModalProps {
    visible: boolean;
    unit: Unit | undefined;
    onClose: () => void;
}

export const UnitsDetailModal = (props: UnitsDetailModalProps) => {
    const dispatch = useThunkDispatch();
    const currentLocale = useAppSelector(state => state.currentLocale);
    const classes = useStyles();

    const isNewUnit = !props.unit?.id || props.unit.id <= 0;
    const initialValues: UnitFormikValues = {
        translations: {
            [currentLocale]: '',
            ...props.unit?.translations,
        },
    };

    const handleClose = (formikProps: FormikProps<UnitFormikValues>) => {
        formikProps.resetForm();
        props.onClose();
    };

    const handleSubmit = (values: UnitFormikValues, formikProps: FormikHelpers<UnitFormikValues>) => {
        props.onClose();

        const unit: Partial<Unit> = {
            id: props.unit?.id,
            translations: { ...values.translations } as LocalizedString,
            order: props.unit?.order || null,
        };

        if (unit?.id && unit.id >= 0) {
            dispatch(patchUnit(unit));
        } else {
            dispatch(createUnit(unit));
        }

        formikProps.resetForm();
    };

    const actionChildren = (formikProps: FormikProps<UnitFormikValues>) => (
        <>
            <PoolButton
                key="1"
                styleType="firstLevel"
                variant="text"
                onClick={() => handleClose(formikProps)}
                className={classes.actionChildren}
            >
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>
            <PoolButton
                key="2"
                styleType="firstLevel"
                onClick={() => formikProps.submitForm()}
                className={classes.actionChildren}
            >
                <FormattedMessage id={isNewUnit ? 'app.common.add' : 'app.common.save'} />
            </PoolButton>
        </>
    );

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            enableReinitialize={true}
            validationSchema={unitsModalValidationSchema(currentLocale)}
        >
            {(formikProps: FormikProps<UnitFormikValues>) => (
                <Form>
                    <PoolModal
                        title={
                            <FormattedMessage
                                id={isNewUnit ? 'app.configuration.createUnit' : 'app.configuration.editUnit'}
                            />
                        }
                        isVisible={props.visible}
                        handleClose={() => handleClose(formikProps)}
                        actionChildren={actionChildren(formikProps)}
                    >
                        <ConfigurationDetailForm />
                    </PoolModal>
                </Form>
            )}
        </Formik>
    );
};
